import { useEffect, useRef } from 'react'
import { Box, Paper, Chip, Typography, IconButton, Button } from '@mui/material'
import {
  Edit,
  Close,
  Delete,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material'
import {
  StructuredQuestion,
  QUESTION_TYPES,
  CONDITIONAL_OPPERATORS,
} from '../interfaces/questions'
import QuestionEditor from './QuestionEditor'

interface QuestionCardProps {
  question: StructuredQuestion
  allQuestions: StructuredQuestion[]
  onEdit: () => void
  onUpdate: (updated: StructuredQuestion) => void
  onDelete?: () => void
  onInsertAbove?: () => void
  onInsertBelow?: () => void
  isEditing: boolean
}

const getQuestionTypeLabel = (type: string): string => {
  const questionType = QUESTION_TYPES.find((qt) => qt.value === type)
  return questionType?.label || type
}

export default function QuestionCard({
  question,
  allQuestions,
  onEdit,
  onUpdate,
  onDelete,
  onInsertAbove,
  onInsertBelow,
  isEditing,
}: QuestionCardProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isEditing && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [isEditing])

  if (isEditing) {
    return (
      <Box ref={containerRef} sx={{ mb: 2, maxWidth: '800px' }}>
        <Box sx={{ mb: 1 }}>
          <Button
            type="button"
            size="small"
            startIcon={<Close />}
            onClick={onEdit}
            variant="outlined"
          >
            Done editing
          </Button>
        </Box>
        <QuestionEditor
          question={question}
          allQuestions={allQuestions}
          onChange={onUpdate}
          onDelete={onDelete || (() => {})}
          isDragging={false}
          hideDelete={true}
        />
      </Box>
    )
  }

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: '#fafafa', maxWidth: '800px' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Chip
              label={question.question_number}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={getQuestionTypeLabel(question.question_type)}
              size="small"
            />
            {question.section && (
              <Chip
                label={`Section: ${question.section}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {onInsertAbove && (
              <Button
                type="button"
                size="small"
                variant="outlined"
                startIcon={<ArrowUpward />}
                onClick={onInsertAbove}
                sx={{ textTransform: 'none' }}
              >
                Add Above
              </Button>
            )}
            {onInsertBelow && (
              <Button
                type="button"
                size="small"
                variant="outlined"
                startIcon={<ArrowDownward />}
                onClick={onInsertBelow}
                sx={{ textTransform: 'none', textAlign: 'center' }}
              >
                Add Below
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <Typography variant="body1" fontWeight="500" sx={{ mb: 1 }}>
        {question.question}
      </Typography>
      {question.question_details && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {question.question_details}
        </Typography>
      )}
      {question.options && question.options.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" fontWeight="500">
            Options:
          </Typography>
          <Box sx={{ ml: 2 }}>
            {question.options.map((opt, idx) => (
              <Typography key={idx} variant="body2">
                • {opt}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        {question.required && (
          <Chip label="Required" size="small" color="error" />
        )}
        {question.attachment_required && (
          <Chip label="Attachment Required" size="small" color="warning" />
        )}
        {question.char_limit && (
          <Chip
            label={`Max ${question.char_limit} chars`}
            size="small"
            variant="outlined"
          />
        )}
        {question.word_limit && (
          <Chip
            label={`Max ${question.word_limit} words`}
            size="small"
            variant="outlined"
          />
        )}
        {question.conditional_on?.question_number && (
          <Chip
            label={[
              `Conditional on: Question ${question.conditional_on.question_number}`,
              CONDITIONAL_OPPERATORS.find(
                (op) => op.value === question.conditional_on!.operator,
              )?.label,
              question.conditional_on.value
                ? `'${question.conditional_on.value}'`
                : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
        {question.depends_on && question.depends_on.question_numbers && (
          <Chip
            label={`Depends on: Questions ${question.depends_on.question_numbers.join(
              ', ',
            )}`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
        {question.has_other_text_field && (
          <Chip
            label={`Text input enabled for 'Other' option`}
            size="small"
            variant="outlined"
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 1 }}>
        {onDelete && (
          <IconButton type="button" size="small" onClick={onDelete} color="error">
            <Delete />
          </IconButton>
        )}
        <IconButton type="button" size="small" onClick={onEdit}>
          <Edit />
        </IconButton>
      </Box>
    </Paper>
  )
}
